import { createSignal, createResource, createMemo, createEffect, For, Show } from 'solid-js';
import { karyawanMe, karyawanLogout, refreshKaryawan } from '../lib/session.js';
import { formatMoney } from '../lib/money.js';
import { APP_VERSION } from '../lib/version.js';
import TimeInput24 from '../components/TimeInput24.jsx';

// KaryawanPortal — the employee self-service portal: view/print your own
// payslips and change your password. While the default password is still in
// use (mustChangePassword), the only thing allowed is changing it — slips are
// hidden until then.

async function getJSON(url) {
  const res = await fetch(url);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || ('HTTP ' + res.status));
  return data;
}

const SCHEME_BADGE = { 'legacy-import': 'info', final: 'success', terbit: 'warning' };

// todayStr — local YYYY-MM-DD (used as the default lembur work date).
const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function todayStr() { return fmtISO(new Date()); }
// periodStart21 — the most recent 21st on/before today (the pay period starts on
// the 21st), used to default the approval inbox to the current period.
function periodStart21() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), 21);
  if (now.getDate() < 21) d.setMonth(d.getMonth() - 1);
  return fmtISO(d);
}

export default function KaryawanPortal() {
  const [view, setView] = createSignal('list'); // 'list' | 'slip' | 'password' | 'saldo'
  const [slip, setSlip] = createSignal(null);
  const k = () => karyawanMe();

  // The employee's own simpanan/pinjaman balances (tab shown only if any exist).
  const [balances] = createResource(
    () => (k() && !k().mustChangePassword) ? 'load' : null,
    () => getJSON('/api/karyawan/balances').catch(() => []),
  );
  const hasBalances = () => (balances() ?? []).length > 0;

  // Master switch: when the admin hides Slip Gaji, never land on the slip views.
  createEffect(() => {
    if (k() && !k().showSlips && (view() === 'list' || view() === 'slip')) setView('lembur');
  });

  return (
    <div class="kary-wrap">
      <header class="kary-top kary-noprint">
        <Show when={k()?.hasLogo} fallback={<div class="brand-mark">MP</div>}>
          <img class="brand-logo" src={`/api/company/${k().companyId}/logo`} alt={k()?.companyName || 'logo'} />
        </Show>
        <div class="kary-id">
          <b>{k()?.name}</b>
          <small>{k()?.nik} · Portal Karyawan</small>
        </div>
        <div class="spacer" />
        <Show when={!k()?.mustChangePassword}>
          <Show when={k()?.showSlips}>
            <button class="btn ghost" classList={{ active: view() === 'list' }} onClick={() => setView('list')}>Slip Gaji</button>
          </Show>
          <button class="btn ghost" classList={{ active: view() === 'absensi' }} onClick={() => setView('absensi')}>Absensi</button>
          <button class="btn ghost" classList={{ active: view() === 'lembur' }} onClick={() => setView('lembur')}>Lembur</button>
          <button class="btn ghost" classList={{ active: view() === 'izin' }} onClick={() => setView('izin')}>Izin / Cuti</button>
          <Show when={hasBalances()}>
            <button class="btn ghost" classList={{ active: view() === 'saldo' }} onClick={() => setView('saldo')}>Saldo</button>
          </Show>
          <Show when={k()?.canApprove}>
            <button class="btn ghost" classList={{ active: view() === 'approvals' }} onClick={() => setView('approvals')}>Persetujuan Lembur</button>
          </Show>
          <button class="btn ghost" classList={{ active: view() === 'password' }} onClick={() => setView('password')}>Ganti Password</button>
        </Show>
        <button class="btn" onClick={karyawanLogout}>Keluar</button>
      </header>

      <main class="kary-main">
        <Show when={k()?.mustChangePassword}>
          <div class="card card-pad kary-notice kary-noprint">
            <h3>Ganti password default Anda</h3>
            <p class="kg-meta-text">
              Anda masih memakai password default <b>"karyawan"</b>. Demi keamanan,
              slip gaji baru bisa dibuka setelah Anda menggantinya.
            </p>
            <ChangePassword forced onDone={refreshKaryawan} />
          </div>
        </Show>

        <Show when={!k()?.mustChangePassword && view() === 'password'}>
          <div class="card card-pad kary-notice kary-noprint">
            <h3>Ganti Password</h3>
            <ChangePassword onDone={() => setView('list')} />
          </div>
        </Show>

        <Show when={!k()?.mustChangePassword && view() === 'list' && k()?.showSlips}>
          <SlipList onOpen={(s) => { setSlip(s); setView('slip'); }} />
        </Show>

        <Show when={!k()?.mustChangePassword && view() === 'slip' && slip() && k()?.showSlips}>
          <div class="kary-noprint" style="margin-bottom:10px">
            <button class="btn ghost" onClick={() => setView('list')}>← Daftar slip</button>
            <button class="btn primary" style="margin-left:8px" onClick={() => window.print()}>Cetak / Unduh PDF</button>
          </div>
          <SlipCard view={slip()} />
        </Show>

        <Show when={!k()?.mustChangePassword && view() === 'absensi'}>
          <MyAbsensi />
        </Show>

        <Show when={!k()?.mustChangePassword && view() === 'lembur'}>
          <LemburView />
        </Show>
        <Show when={!k()?.mustChangePassword && view() === 'izin'}>
          <IzinView />
        </Show>
        <Show when={!k()?.mustChangePassword && view() === 'saldo'}>
          <SaldoView balances={balances() ?? []} />
        </Show>
        <Show when={!k()?.mustChangePassword && view() === 'approvals'}>
          <ApprovalsView />
        </Show>
      </main>
      <footer class="kary-foot kary-noprint">Payroll Metalindo · v{APP_VERSION}</footer>
    </div>
  );
}

const STATUS_TONE = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'info' };
const STATUS_LABEL = { pending: 'menunggu', approved: 'disetujui', rejected: 'ditolak', cancelled: 'dibatalkan' };

// LemburView — submit a lembur request (+ proof files) and see your own history.
// When the employee's department/policy forbids overtime (canSubmitLembur=false),
// the submission form is replaced by a notice; past requests stay visible.
function LemburView() {
  const [mine, { refetch }] = createResource(() => getJSON('/api/karyawan/lembur/mine'));
  const [msg, setMsg] = createSignal(null);
  const canSubmit = () => karyawanMe()?.canSubmitLembur;

  async function cancel(id) {
    if (!confirm('Batalkan pengajuan ini?')) return;
    const res = await fetch('/api/karyawan/lembur/cancel', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }),
    });
    if (res.ok) refetch(); else setMsg({ ok: false, t: (await res.json().catch(() => ({}))).error });
  }

  return (
    <>
      <div class="card card-pad">
        <h3 class="kg-hist-title">Ajukan Lembur</h3>
        <Show when={canSubmit()} fallback={
          <p class="kg-meta-text">Pengajuan lembur tidak tersedia untuk Anda. Bila ini keliru, hubungi admin/HR.</p>
        }>
          <LemburForm onDone={() => { refetch(); setMsg({ ok: true, t: 'Pengajuan terkirim, menunggu persetujuan.' }); }} />
          <Show when={msg()}><div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin-top:10px">{msg().t}</div></Show>
        </Show>
      </div>

      <div class="card card-pad" style="margin-top:12px">
        <h3 class="kg-hist-title">Pengajuan Saya</h3>
        <Show when={mine() && mine().length === 0}><p class="kg-meta-text">Belum ada pengajuan.</p></Show>
        <For each={mine() ?? []}>{(r) =>
          <div class="kary-lembur-row">
            <div class="kary-lembur-when">
              <b>{r.workDate}</b> · {r.start}–{r.end}
              <Show when={r.approvedStart}>
                <span class="lb-approved-note"> → disetujui {r.approvedStart}–{r.approvedEnd}</span>
              </Show>
              <Show when={r.targetApproverName}>
                <div class="kg-meta-text">Penyetuju: {r.targetApproverName}</div>
              </Show>
              <Show when={r.reason}><div class="kg-meta-text lb-reason">{r.reason}</div></Show>
              <LemburFiles files={r.files} />
            </div>
            <div class="spacer" />
            <span class={`badge ${STATUS_TONE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
            <Show when={r.status === 'rejected' && r.decisionNote}>
              <div class="kg-meta-text" style="max-width:180px">alasan: {r.decisionNote}</div>
            </Show>
            <Show when={r.status === 'pending'}>
              <button class="btn ghost" onClick={() => cancel(r.id)}>batal</button>
            </Show>
          </div>
        }</For>
      </div>
    </>
  );
}

// ApprovalsView — the LEMBUR (overtime) approval inbox for this approver, via
// the structural SPV/Manager/Direksi chain. A searchable/filterable table (date
// period + keterangan/name/NIK) with per-row checkboxes + check-all and a bulk
// "Setujui terpilih" button; reject stays per-row (each needs its own reason).
// Leave (izin/sakit/cuti) approval is NOT here — it is centralized to HR admin
// in the main app; employees only submit leave from the Izin / Cuti tab.
function ApprovalsView() {
  const [lembur, { refetch }] = createResource(() => getJSON('/api/karyawan/lembur/approvals'));
  // Default to the current pay period: from the most recent 21st through today.
  const [from, setFrom] = createSignal(periodStart21());
  const [to, setTo] = createSignal(todayStr());
  const [q, setQ] = createSignal('');
  const [checked, setChecked] = createSignal(new Set());
  const [busy, setBusy] = createSignal(false);
  // Per-row approved-hours edits (id → {start,end}); default to the requested
  // window. The approver tweaks these and clicks the row's Setujui to override.
  const [editTimes, setEditTimes] = createSignal({});
  const timesOf = (r) => editTimes()[r.id] ?? { start: r.start, end: r.end };
  function setTime(r, field, val) {
    setEditTimes({ ...editTimes(), [r.id]: { ...timesOf(r), [field]: val } });
  }

  const filtered = createMemo(() => {
    const f = from(), t = to(), kw = q().trim().toLowerCase();
    return (lembur() ?? []).filter((r) =>
      (!f || r.workDate >= f) && (!t || r.workDate <= t) &&
      (!kw || (r.reason || '').toLowerCase().includes(kw)
            || (r.name || '').toLowerCase().includes(kw)
            || (r.nik || '').toLowerCase().includes(kw)));
  });
  const selectedCount = createMemo(() => filtered().filter((r) => checked().has(r.id)).length);
  const allChecked = createMemo(() => filtered().length > 0 && filtered().every((r) => checked().has(r.id)));

  function toggleAll(on) {
    const s = new Set(checked());
    for (const r of filtered()) on ? s.add(r.id) : s.delete(r.id);
    setChecked(s);
  }
  function toggle(id, on) {
    const s = new Set(checked());
    on ? s.add(id) : s.delete(id);
    setChecked(s);
  }

  async function reject(id) {
    const note = prompt('Alasan penolakan:') || '';
    if (!note.trim()) return;
    setBusy(true);
    const res = await fetch('/api/karyawan/lembur/decide', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, approve: false, note }),
    });
    setBusy(false);
    if (res.ok) refetch(); else alert((await res.json().catch(() => ({}))).error);
  }
  async function approveSelected() {
    const ids = filtered().filter((r) => checked().has(r.id)).map((r) => r.id);
    if (ids.length === 0) return;
    setBusy(true);
    const res = await fetch('/api/karyawan/lembur/approve-bulk', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    setBusy(false);
    setChecked(new Set());
    if (res.ok) refetch(); else alert((await res.json().catch(() => ({}))).error);
  }
  // Approve one request, honoring any edited hours (the server treats an
  // unchanged window as "approved as requested").
  async function approveOne(r) {
    const t = timesOf(r);
    setBusy(true);
    const res = await fetch('/api/karyawan/lembur/decide', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: r.id, approve: true, overrideStart: t.start, overrideEnd: t.end }),
    });
    setBusy(false);
    if (res.ok) refetch(); else alert((await res.json().catch(() => ({}))).error);
  }

  return (
    <div class="card card-pad">
      <div class="ky-vars-head">
        <h3 class="kg-hist-title">Persetujuan Lembur</h3>
        <div class="spacer" />
        <button class="btn primary" disabled={busy() || selectedCount() === 0} onClick={approveSelected}>
          Setujui terpilih ({selectedCount()})
        </button>
      </div>

      <p class="ky-vars-note">
        Centang lalu <b>Setujui terpilih</b> menyetujui sesuai jam yang diajukan. Untuk mengubah jam
        yang disetujui, edit kolom <b>Jam</b> pada baris itu lalu klik <b>Setujui</b>.
      </p>

      <div class="lb-filter">
        <label class="kg-field"><span>Dari tanggal</span>
          <input type="date" value={from()} onInput={(e) => setFrom(e.currentTarget.value)} /></label>
        <label class="kg-field"><span>Sampai tanggal</span>
          <input type="date" value={to()} onInput={(e) => setTo(e.currentTarget.value)} /></label>
        <label class="kg-field lb-search"><span>Cari keterangan / nama / NIK</span>
          <input type="text" placeholder="kata kunci…" value={q()} onInput={(e) => setQ(e.currentTarget.value)} /></label>
      </div>

      <Show when={lembur() && filtered().length === 0}>
        <p class="kg-meta-text">Tidak ada pengajuan lembur yang menunggu.</p>
      </Show>

      <Show when={filtered().length > 0}>
        <div class="pg-grid-wrap">
          <table class="data">
            <thead>
              <tr>
                <th class="lb-check"><input type="checkbox" checked={allChecked()} onInput={(e) => toggleAll(e.currentTarget.checked)} /></th>
                <th>Karyawan</th><th>Tanggal</th><th>Jam</th><th>Keterangan</th><th>Bukti</th><th></th>
              </tr>
            </thead>
            <tbody>
              <For each={filtered()}>{(r) =>
                <tr classList={{ 'lb-picked': checked().has(r.id) }}>
                  <td class="lb-check"><input type="checkbox" checked={checked().has(r.id)} onInput={(e) => toggle(r.id, e.currentTarget.checked)} /></td>
                  <td><div class="kg-code">{r.nik}</div><div class="kg-sub">{r.name}</div><Show when={r.department}><div class="kg-sub">{r.department}</div></Show></td>
                  <td class="ky-nik">{r.workDate}</td>
                  <td>
                    <div class="lb-time-edit">
                      <TimeInput24 value={timesOf(r).start} disabled={busy()} onChange={(v) => setTime(r, 'start', v)} />
                      <span>–</span>
                      <TimeInput24 value={timesOf(r).end} disabled={busy()} onChange={(v) => setTime(r, 'end', v)} />
                    </div>
                  </td>
                  <td><Show when={r.reason} fallback={<span class="vg-dim">—</span>}><span class="lb-reason">{r.reason}</span></Show></td>
                  <td><LemburFiles files={r.files} api="lembur" /></td>
                  <td class="num">
                    <div class="lb-actions">
                      <button class="btn primary" disabled={busy()} onClick={() => approveOne(r)}>Setujui</button>
                      <button class="btn ghost" disabled={busy()} onClick={() => reject(r.id)}>Tolak</button>
                    </div>
                  </td>
                </tr>
              }</For>
            </tbody>
          </table>
        </div>
      </Show>
    </div>
  );
}

// IzinView — submit an Izin/Cuti/Sakit request (+ proof) and see your history.
function IzinView() {
  const [mine, { refetch }] = createResource(() => getJSON('/api/karyawan/izin/mine'));
  const [msg, setMsg] = createSignal(null);

  async function cancel(id) {
    if (!confirm('Batalkan pengajuan ini?')) return;
    const res = await fetch('/api/karyawan/izin/cancel', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }),
    });
    if (res.ok) refetch(); else setMsg({ ok: false, t: (await res.json().catch(() => ({}))).error });
  }

  return (
    <>
      <div class="card card-pad">
        <h3 class="kg-hist-title">Ajukan Izin / Cuti</h3>
        <IzinForm onDone={() => { refetch(); setMsg({ ok: true, t: 'Pengajuan terkirim, menunggu persetujuan.' }); }} />
        <Show when={msg()}><div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin-top:10px">{msg().t}</div></Show>
      </div>

      <div class="card card-pad" style="margin-top:12px">
        <h3 class="kg-hist-title">Pengajuan Saya</h3>
        <Show when={mine() && mine().length === 0}><p class="kg-meta-text">Belum ada pengajuan.</p></Show>
        <For each={mine() ?? []}>{(r) =>
          <div class="kary-lembur-row">
            <div class="kary-lembur-when">
              <span class="badge info">{r.kind}</span> <b>{r.fromDate}{r.toDate !== r.fromDate ? ` – ${r.toDate}` : ''}</b>
              <Show when={r.reason}><div class="kg-meta-text lb-reason">{r.reason}</div></Show>
              <LemburFiles files={r.files} api="izin" />
            </div>
            <div class="spacer" />
            <span class={`badge ${STATUS_TONE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
            <Show when={r.status === 'rejected' && r.decisionNote}>
              <div class="kg-meta-text" style="max-width:180px">alasan: {r.decisionNote}</div>
            </Show>
            <Show when={r.status === 'pending'}>
              <button class="btn ghost" onClick={() => cancel(r.id)}>batal</button>
            </Show>
          </div>
        }</For>
      </div>
    </>
  );
}

// IzinForm — kind + date range + reason + proof upload.
function IzinForm(props) {
  const [kind, setKind] = createSignal('Cuti');
  const [from, setFrom] = createSignal(todayStr());
  const [to, setTo] = createSignal(todayStr());
  const [reason, setReason] = createSignal('');
  const [files, setFiles] = createSignal([]);
  const [busy, setBusy] = createSignal(false);
  const [err, setErr] = createSignal('');
  let fileInput;

  async function submit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    const fd = new FormData();
    fd.set('kind', kind()); fd.set('fromDate', from()); fd.set('toDate', to() || from()); fd.set('reason', reason());
    for (const f of files()) fd.append('files', f);
    const res = await fetch('/api/karyawan/izin', { method: 'POST', body: fd });
    setBusy(false);
    if (res.ok) {
      setFrom(todayStr()); setTo(todayStr()); setReason(''); setFiles([]);
      if (fileInput) fileInput.value = '';
      props.onDone?.();
    } else setErr((await res.json().catch(() => ({}))).error || 'gagal');
  }

  return (
    <form class="kary-lembur-form" onSubmit={submit}>
      <div class="kary-lembur-grid">
        <label class="kg-field"><span>Jenis</span>
          <select class="kg-select" value={kind()} onInput={(e) => setKind(e.currentTarget.value)}>
            <option value="Cuti">Cuti</option>
            <option value="Izin">Izin</option>
            <option value="Sakit">Sakit</option>
            <option value="Tugas">Tugas</option>
          </select></label>
        <label class="kg-field"><span>Dari tanggal</span>
          <input type="date" value={from()} onInput={(e) => setFrom(e.currentTarget.value)} required /></label>
        <label class="kg-field"><span>Sampai tanggal</span>
          <input type="date" value={to()} onInput={(e) => setTo(e.currentTarget.value)} /></label>
      </div>
      <label class="kg-field"><span>Keterangan</span>
        <input type="text" placeholder="alasan izin / cuti" value={reason()} onInput={(e) => setReason(e.currentTarget.value)} /></label>
      <label class="kg-field"><span>Bukti (surat dokter, dll. — opsional)</span>
        <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.heic,.pdf" ref={fileInput}
          onInput={(e) => setFiles(Array.from(e.currentTarget.files))} /></label>
      <Show when={files().length > 0}><div class="kg-meta-text">{files().length} berkas dipilih</div></Show>
      <Show when={err()}><div class="kg-verdict fail">{err()}</div></Show>
      <button class="btn primary" type="submit" disabled={busy()}>{busy() ? 'Mengirim…' : 'Ajukan'}</button>
    </form>
  );
}

// LemburFiles — proof attachment links (open in a new tab).
function LemburFiles(props) {
  return (
    <Show when={(props.files ?? []).length > 0}>
      <div class="kary-files">
        <For each={props.files}>{(f) =>
          <a class="kary-file" href={`/api/karyawan/${props.api ?? 'lembur'}/file/${f.id}`} target="_blank" rel="noopener">📎 {f.origName}</a>
        }</For>
      </div>
    </Show>
  );
}

// LemburForm — time/reason + multi-file proof upload. The date is ALWAYS today
// (HR rule: a portal lembur request can only be for the same day) — shown as a
// read-only label; the employee only sets the hours (default 16:00–17:00). The
// backend re-checks the date.
function LemburForm(props) {
  const [start, setStart] = createSignal('16:00');
  const [end, setEnd] = createSignal('17:00');
  const [reason, setReason] = createSignal('');
  const [files, setFiles] = createSignal([]);
  const [busy, setBusy] = createSignal(false);
  const [err, setErr] = createSignal('');
  const [approver, setApprover] = createSignal('');
  const [approverTouched, setApproverTouched] = createSignal(false);
  const [approvers] = createResource(() => getJSON('/api/karyawan/lembur/approvers'));
  let fileInput;

  // Pre-select the recommended approver (own-department SPV/Manager) once the
  // list loads, unless the employee already changed it.
  createEffect(() => {
    const list = approvers();
    if (!list || approverTouched()) return;
    const rec = list.find((a) => a.recommended) || list[0];
    if (rec) setApprover(rec.nik);
  });
  const approverLabel = (a) => `${a.name}${a.position ? ' — ' + a.position : ''}`;

  async function submit(e) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    const fd = new FormData();
    fd.set('workDate', todayStr()); fd.set('start', start()); fd.set('end', end()); fd.set('reason', reason());
    fd.set('approverNik', approver());
    for (const f of files()) fd.append('files', f);
    const res = await fetch('/api/karyawan/lembur', { method: 'POST', body: fd });
    setBusy(false);
    if (res.ok) {
      setStart('16:00'); setEnd('17:00'); setReason(''); setFiles([]);
      setApproverTouched(false);
      if (fileInput) fileInput.value = '';
      props.onDone?.();
    } else setErr((await res.json().catch(() => ({}))).error || 'gagal');
  }

  return (
    <form class="kary-lembur-form" onSubmit={submit}>
      <div class="kary-lembur-grid">
        <label class="kg-field"><span>Tanggal</span>
          <div class="kary-lembur-today">{todayStr()} <span class="kg-meta-text">(hari ini)</span></div></label>
        <label class="kg-field"><span>Jam mulai</span>
          <TimeInput24 value={start()} onChange={setStart} required /></label>
        <label class="kg-field"><span>Jam selesai</span>
          <TimeInput24 value={end()} onChange={setEnd} required /></label>
      </div>
      <label class="kg-field"><span>Disetujui oleh</span>
        <Show
          when={(approvers() ?? []).length > 0}
          fallback={<div class="kg-meta-text">Atasan ditentukan otomatis.</div>}
        >
          <select class="vg-select" value={approver()}
            onInput={(e) => { setApproverTouched(true); setApprover(e.currentTarget.value); }}>
            <For each={approvers()}>{(a) =>
              <option value={a.nik}>{approverLabel(a)}{a.recommended ? ' (atasan departemen)' : ''}</option>
            }</For>
          </select>
        </Show>
      </label>
      <label class="kg-field"><span>Keterangan</span>
        <textarea class="kary-reason" rows="3" placeholder="alasan / pekerjaan lembur (boleh beberapa baris)"
          value={reason()} onInput={(e) => setReason(e.currentTarget.value)} /></label>
      <label class="kg-field"><span>Bukti (foto / dokumen — surat tugas, dll.)</span>
        <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.heic,.pdf" ref={fileInput}
          onInput={(e) => setFiles(Array.from(e.currentTarget.files))} /></label>
      <Show when={files().length > 0}><div class="kg-meta-text">{files().length} berkas dipilih</div></Show>
      <Show when={err()}><div class="kg-verdict fail">{err()}</div></Show>
      <button class="btn primary" type="submit" disabled={busy()}>{busy() ? 'Mengirim…' : 'Ajukan Lembur'}</button>
    </form>
  );
}

// SlipList — the employee's payslips; click to open.
function SlipList(props) {
  const [runs] = createResource(() => getJSON('/api/karyawan/slips'));
  const [loadingId, setLoadingId] = createSignal(0);

  async function open(runId) {
    setLoadingId(runId);
    try {
      props.onOpen(await getJSON(`/api/karyawan/slip/${runId}`));
    } catch (e) {
      alert(e.message);
    }
    setLoadingId(0);
  }

  return (
    <div class="card card-pad">
      <h3 class="kg-hist-title">Slip Gaji Saya</h3>
      <Show when={runs() && runs().length === 0}>
        <p class="kg-meta-text">Belum ada slip gaji.</p>
      </Show>
      <div class="kary-slips">
        <For each={runs() ?? []}>{(r) =>
          <button class="kary-slip-row" disabled={loadingId() === r.runId} onClick={() => open(r.runId)}>
            <span class="kary-slip-period">{r.periodStart} — {r.periodEnd}</span>
            <span class={`badge ${SCHEME_BADGE[r.status] ?? 'info'}`}>{r.scheme}</span>
            <Show when={r.tipeLabel}><span class="badge info">{r.tipeLabel}</span></Show>
            <Show when={r.status === 'terbit'}><span class="badge warning">SEMENTARA</span></Show>
            <span class="spacer" />
            <span class="kary-slip-go">{loadingId() === r.runId ? 'memuat…' : 'lihat →'}</span>
          </button>
        }</For>
      </div>
    </div>
  );
}

// SlipCard — the printable slip (matches the admin slip layout).
function SlipCard(props) {
  const v = () => props.view;
  const s = () => props.view.slip;
  return (
    <div class="card slip kary-slip">
      <Show when={v().run.status === 'terbit'}>
        <div class="slip-tentatif">⚠ SLIP SEMENTARA — belum final. Bila ada keberatan, hubungi admin sebelum difinalisasi.</div>
      </Show>
      <div class="slip-head">
        <div class="slip-head-id">
          <Show when={v().hasLogo}>
            <img class="slip-logo" src={`/api/company/${v().companyId}/logo`} alt="" />
          </Show>
          <div>
            <div class="slip-brand">{v().company}</div>
            <Show when={v().address}><div class="slip-title">{v().address}</div></Show>
          </div>
        </div>
        <div class="slip-period">
          {v().run.tipeLabel ? `SLIP ${v().run.tipeLabel.toUpperCase()}` : 'SLIP GAJI'}<br />{v().run.periodStart} — {v().run.periodEnd}<br />{v().run.scheme}
        </div>
      </div>
      <div class="slip-emp">
        <b>{s().name}</b> · {s().nik}
        <Show when={s().position}> · {s().position}</Show>
        <Show when={s().department}> · {s().department}</Show>
      </div>
      <div class="slip-cols">
        <div>
          <div class="slip-sec">PENDAPATAN</div>
          <For each={s().earnings}>{(l) =>
            <div class="slip-line" classList={{ 'slip-skip': l.skipTotal }}>
              <span>{l.label}</span><span class="money">{formatMoney(l.amount)}{l.skipTotal ? ' *' : ''}</span>
            </div>
          }</For>
        </div>
        <div>
          <div class="slip-sec">POTONGAN</div>
          <For each={s().deductions}>{(l) =>
            <div class="slip-line" classList={{ 'slip-skip': l.skipTotal }}>
              <span>{l.label}</span><span class="money">{formatMoney(l.amount)}{l.skipTotal ? ' *' : ''}</span>
            </div>
          }</For>
          <Show when={s().deductions.length === 0}><div class="slip-line"><span class="vg-dim">—</span></div></Show>
        </div>
      </div>
      <div class="slip-totals">
        <div class="slip-line"><span>Total Pendapatan</span><span class="money">{formatMoney(s().bruto)}</span></div>
        <div class="slip-line"><span>Total Potongan</span><span class="money">{formatMoney(s().potongan)}</span></div>
        <div class="slip-line slip-net"><span>GAJI BERSIH</span><span class="money">{formatMoney(s().bersih)}</span></div>
        <Show when={s().earnings.some((l) => l.skipTotal) || s().deductions.some((l) => l.skipTotal)}>
          <div class="slip-skip-note">* tidak termasuk dalam total</div>
        </Show>
      </div>
      <Show when={s().notes.length > 0}>
        <div class="slip-notes">
          <For each={s().notes}>{(n) => <div class="slip-note">{n.label}: {n.text}</div>}</For>
        </div>
      </Show>
      <Show when={s().bankName}>
        <div class="slip-bank">Transfer ke {s().bankName} {s().bankAccount}</div>
      </Show>
      <Show when={v().footer}><div class="slip-note slip-bank">{v().footer}</div></Show>
    </div>
  );
}

// MyAbsensi — the employee's OWN raw scans per month: evidence, not verdicts
// (no Hadir/Alpha labels — the authoritative interpretation lives in the
// published slip). Lets an employee catch a forgotten scan pulang while it is
// still correctable. Days without any scan are omitted.
const ABS_DAYS = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
function MyAbsensi() {
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = createSignal(thisMonth);
  const [data] = createResource(month, (m) => getJSON(`/api/karyawan/absensi?month=${encodeURIComponent(m)}`));
  const [open, setOpen] = createSignal(''); // date whose full scan list is expanded
  const dayName = (d) => { const t = new Date(d + 'T00:00:00'); return isNaN(t) ? '' : ABS_DAYS[t.getDay()]; };

  return (
    <div class="card card-pad">
      <div class="ky-vars-head">
        <h3 class="kg-hist-title">Absensi Saya</h3>
        <Show when={data()}>
          <span class="kg-meta-text" style="margin-left:8px">{data().days.length} hari · {data().totalScans} scan</span>
        </Show>
        <div class="spacer" />
        <label class="kg-field">
          <span>Bulan</span>
          <input type="month" value={month()} onInput={(e) => { setMonth(e.currentTarget.value); setOpen(''); }} />
        </label>
      </div>
      <p class="ky-vars-note">
        Daftar scan mesin finger. Hari tanpa scan tidak ditampilkan.
      </p>
      <Show when={data.error}>
        <div class="kg-verdict fail">{String(data.error.message || data.error)}</div>
      </Show>
      <Show when={data()}>
        <Show when={data().hasFid} fallback={
          <p class="kg-meta-text">
            Absensi Anda tidak memakai mesin sidik jari (mode: {data().attendMode}) —
            tidak ada rekaman scan untuk ditampilkan.
          </p>
        }>
          <Show when={data().days.length > 0} fallback={
            <p class="kg-meta-text">Tidak ada scan pada bulan ini.</p>
          }>
            <table class="data">
              <thead>
                <tr><th>Tanggal</th><th>Masuk</th><th>Pulang</th><th class="num">Scan</th></tr>
              </thead>
              <tbody>
                <For each={data().days}>{(d) =>
                  <>
                    <tr style="cursor:pointer" onClick={() => setOpen(open() === d.date ? '' : d.date)}>
                      <td>{d.date} <span class="kg-meta-text">{dayName(d.date)}</span></td>
                      <td class="ky-nik">{d.masuk || '—'}</td>
                      <td class="ky-nik">
                        <Show when={d.pulang} fallback={<span class="badge warning" title="Hanya ada satu scan hari ini — kemungkinan lupa scan pulang">hanya 1 scan</span>}>
                          {d.pulang}
                        </Show>
                      </td>
                      <td class="num">{d.scans.length}</td>
                    </tr>
                    <Show when={open() === d.date}>
                      <tr>
                        <td colspan="4">
                          <div class="kg-meta-text" style="padding:2px 0 6px">
                            <For each={d.scans}>{(sc) =>
                              <span style="display:inline-block; margin-right:14px">
                                {sc.time}<Show when={sc.tipe}> <span style="opacity:0.7">({sc.tipe})</span></Show>
                              </span>
                            }</For>
                          </div>
                        </td>
                      </tr>
                    </Show>
                  </>
                }</For>
              </tbody>
            </table>
          </Show>
        </Show>
      </Show>
    </div>
  );
}

// SaldoView — the employee's own Simpanan/Pinjaman balances + history (read-only).
function SaldoView(props) {
  return (
    <>
      <Show when={(props.balances ?? []).length === 0}>
        <div class="card card-pad"><p class="kg-meta-text">Belum ada saldo simpanan / pinjaman.</p></div>
      </Show>
      <For each={props.balances ?? []}>{(b) => <SaldoCard balance={b} />}</For>
    </>
  );
}

// SaldoCard — one balance with its current saldo and a collapsible history that
// loads on first expand. Read-only: corrections are made by the admin.
function SaldoCard(props) {
  const b = props.balance;
  const [open, setOpen] = createSignal(false);
  const [data] = createResource(
    () => open() ? b.name : null,
    (name) => getJSON(`/api/karyawan/balance/${encodeURIComponent(name)}/txns`),
  );
  return (
    <div class="card card-pad" style="margin-top:12px">
      <div class="ky-vars-head">
        <h3 class="kg-hist-title">{b.name} <span class={`badge ${b.kind === 'pinjaman' ? 'danger' : 'success'}`}>{b.kind}</span></h3>
        <div class="spacer" />
        <span class="kg-meta-text">Saldo saat ini: <b class="money">{formatMoney(b.saldo)}</b></span>
        <button class="btn ghost" onClick={() => setOpen(!open())}>{open() ? 'tutup' : 'riwayat'}</button>
      </div>
      <Show when={open()}>
        <table class="data">
          <thead><tr><th>Tanggal</th><th>Operasi</th><th class="num">Jumlah</th><th class="num">Saldo</th><th>Sumber</th></tr></thead>
          <tbody>
            <For each={data()?.txns ?? []}>{(t) =>
              <tr>
                <td>{t.effDate}</td>
                <td><span class={`badge ${t.op === 'kurang' ? 'danger' : t.op === 'tambah' ? 'success' : 'info'}`}>{t.op}</span></td>
                <td class="num money">{formatMoney(t.amount)}</td>
                <td class="num money">{formatMoney(t.after)}</td>
                <td class="kg-sub">{t.runId ? `Gaji #${t.runId}` : 'Manual'}<Show when={t.note}> · {t.note}</Show></td>
              </tr>
            }</For>
            <Show when={data() && (data().txns ?? []).length === 0}>
              <tr><td colspan="5" class="kg-meta-text">Belum ada transaksi.</td></tr>
            </Show>
          </tbody>
        </table>
      </Show>
    </div>
  );
}

// ChangePassword — current + new + confirm; lifts read-only on success.
function ChangePassword(props) {
  const [cur, setCur] = createSignal(props.forced ? 'karyawan' : '');
  const [next, setNext] = createSignal('');
  const [confirm, setConfirm] = createSignal('');
  const [msg, setMsg] = createSignal(null);
  const [busy, setBusy] = createSignal(false);

  async function submit(e) {
    e.preventDefault();
    setMsg(null);
    if (next() !== confirm()) { setMsg({ ok: false, t: 'Konfirmasi password tidak cocok.' }); return; }
    setBusy(true);
    const res = await fetch('/api/karyawan/password', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword: cur(), newPassword: next() }),
    });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (res.ok) {
      setMsg({ ok: true, t: 'Password berhasil diganti.' });
      setCur(''); setNext(''); setConfirm('');
      props.onDone?.();
    } else setMsg({ ok: false, t: data.error || 'gagal' });
  }

  return (
    <form class="kary-pwd" onSubmit={submit}>
      <Show when={!props.forced}>
        <label class="kg-field"><span>Password saat ini</span>
          <input type="password" value={cur()} onInput={(e) => setCur(e.currentTarget.value)} /></label>
      </Show>
      <label class="kg-field"><span>Password baru</span>
        <input type="password" value={next()} onInput={(e) => setNext(e.currentTarget.value)} /></label>
      <label class="kg-field"><span>Ulangi password baru</span>
        <input type="password" value={confirm()} onInput={(e) => setConfirm(e.currentTarget.value)} /></label>
      <Show when={msg()}>
        <div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`}>{msg().t}</div>
      </Show>
      <button class="btn primary" type="submit" disabled={busy()}>{busy() ? 'Menyimpan…' : 'Simpan password'}</button>
    </form>
  );
}
